import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Globe, Copy, CheckCircle2, ImagePlus, RotateCcw, Download } from "lucide-react";

const SLOTS = [
  { key: "logo", label: "Login Logo", hint: "Square PNG/SVG. Shown on the login panel and mobile drawer.", accept: "image/*" },
  { key: "feature_photo", label: "Login Feature Photo", hint: "Tall/wide JPG. Full-height on desktop right panel.", accept: "image/*" },
  { key: "mobile_bg", label: "Mobile Login Banner", hint: "Landscape JPG. Shown above the form on phones.", accept: "image/*" },
  { key: "favicon", label: "Browser Favicon", hint: "Square PNG (32×32 or larger).", accept: "image/*" },
];

const resolve = (u) => (u?.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

function BrandingCard({ slot, currentUrl, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/branding/${slot.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${slot.label} updated`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!confirm(`Reset ${slot.label} to default?`)) return;
    setBusy(true);
    try {
      await api.delete(`/branding/${slot.key}`);
      toast.success("Reset to default");
      onChange();
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`branding-slot-${slot.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-heading font-semibold text-[#001F54]">{slot.label}</div>
          <div className="text-xs text-slate-500 mt-0.5">{slot.hint}</div>
        </div>
        <button onClick={reset} className="text-slate-400 hover:text-[#003DA5] p-1" title="Reset to default" data-testid={`branding-reset-${slot.key}`}>
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 aspect-video bg-[#F3F7FC] border border-slate-200 rounded-lg overflow-hidden grid place-items-center">
        {currentUrl ? <img src={currentUrl} alt={slot.label} className="w-full h-full object-cover" /> : <span className="text-xs text-slate-400">No image</span>}
      </div>
      <label className={`mt-3 h-10 border border-slate-300 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold hover:border-[#003DA5] cursor-pointer transition ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid={`branding-upload-${slot.key}`}>
        <ImagePlus className="w-4 h-4" />
        {busy ? "Uploading…" : "Replace image"}
        <input type="file" accept={slot.accept} className="hidden" onChange={onFile} />
      </label>
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [brand, setBrand] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";

  const loadBrand = () => api.get("/branding").then(r => setBrand(r.data));
  useEffect(() => {
    api.get("/settings").then(r => setS(r.data));
    loadBrand();
  }, []);
  if (!s || !brand) return <div>Loading…</div>;

  const upd = (k) => (e) => setS({ ...s, [k]: Number(e.target.value) });
  const save = async () => {
    try { await api.put("/settings", { active_days: s.active_days, followup_days_min: s.followup_days_min, followup_days_max: s.followup_days_max, inactive_days: s.inactive_days }); toast.success("Saved"); }
    catch { toast.error("Failed"); }
  };

  const resetDemo = async () => {
    if (!confirm("This will wipe ALL demo sales, visits, training reports, tasks, trips, photos & customers, then reload the sample data. Continue?")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data");
      const total = Object.values(data.wiped).reduce((a, b) => a + b, 0);
      toast.success(`Wiped ${total} records and re-seeded sample data`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  const copy = (label, txt) => { navigator.clipboard.writeText(txt); setCopied(label); setTimeout(() => setCopied(""), 1500); };

  const F = ({ label, k }) => (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label>
      <input type="number" value={s[k]} onChange={upd(k)} className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-lg" data-testid={`setting-${k}`} />
    </div>
  );
  const appHost = (process.env.REACT_APP_BACKEND_URL || "").replace(/^https?:\/\//, "");
  const CopyRow = ({ label, value }) => (
    <div className="flex items-center justify-between bg-[#F3F7FC] border border-slate-200 rounded-lg px-3 py-2 text-sm">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
        <div className="font-mono text-slate-800 break-all">{value}</div>
      </div>
      <button onClick={() => copy(label, value)} className="shrink-0 ml-3 h-8 w-8 grid place-items-center border border-slate-300 rounded-md hover:border-[#003DA5] transition">
        {copied === label ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Settings</h1>
        <p className="text-slate-600 mt-1">Branding, classification rules, demo data & custom domain</p>
      </div>

      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F3F7FC] border border-[#BFCFE8] grid place-items-center shrink-0">
              <ImagePlus className="w-5 h-5 text-[#003DA5]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-heading font-semibold">Login Branding</h3>
              <p className="text-sm text-slate-600 mt-1">Upload the official MLILY logo, feature photo, mobile banner and browser favicon. Changes take effect on the next login page load.</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SLOTS.map(sl => (
                  <BrandingCard key={sl.key} slot={sl} currentUrl={resolve(brand[`${sl.key}_url`])} onChange={loadBrand} />
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold text-[#001F54]">Brand Kit Export</div>
                  <div className="text-xs text-slate-500 mt-0.5">ZIP with the current logo, palette hex codes and Montserrat font links — for external designers.</div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const r = await api.get("/branding/kit", { responseType: "blob" });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a"); a.href = url; a.download = "MLILY-CRM-Brand-Kit.zip"; a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Brand kit downloaded");
                    } catch { toast.error("Download failed"); }
                  }}
                  className="h-10 px-4 rounded-lg border border-slate-300 font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5] transition"
                  data-testid="download-brand-kit"
                >
                  <Download className="w-4 h-4" /> Download Brand Kit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl space-y-4">
        <h3 className="text-lg font-heading font-semibold">Customer Activity Rules</h3>
        <F label="Active if ordered within (days)" k="active_days" />
        <F label="Follow-up required from (days)" k="followup_days_min" />
        <F label="Follow-up required until (days)" k="followup_days_max" />
        <F label="Inactive from (days)" k="inactive_days" />
        <div className="pt-4"><button onClick={save} className="mlily-btn h-11 px-6 rounded-lg font-semibold" data-testid="save-settings-btn">Save Rules</button></div>
      </div>

      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 grid place-items-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-heading font-semibold">Reset Demo Data</h3>
              <p className="text-sm text-slate-600 mt-1">
                Wipe every demo <b>sale, visit, training report, task, trip, photo and customer</b>, then reload the pre-configured sample data. <b>User accounts are preserved.</b>
              </p>
              <button
                onClick={resetDemo}
                disabled={resetting}
                className="mt-4 h-11 px-5 rounded-lg font-semibold border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-2 transition disabled:opacity-60"
                data-testid="reset-demo-btn"
              >
                <RefreshCw className={`w-4 h-4 ${resetting ? "animate-spin" : ""}`} />
                {resetting ? "Resetting…" : "Wipe & Re-seed Demo Data"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#001F54] text-white grid place-items-center shrink-0">
            <Globe className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold">Custom Domain Setup</h3>
            <p className="text-sm text-slate-600 mt-1">Point your MLILY-owned domain (for example <b>crm.mlily.com.au</b>) at this CRM.</p>
            <div className="mt-4 space-y-2">
              <CopyRow label="Type" value="CNAME" />
              <CopyRow label="Host / Name" value="crm" />
              <CopyRow label="Points to (Value)" value={appHost} />
              <CopyRow label="TTL" value="3600" />
            </div>
            <ol start="3" className="mt-4 space-y-2 text-sm text-slate-700 list-decimal list-inside">
              <li>Save the DNS record. Propagation typically takes 5–60 minutes.</li>
              <li>In your hosting dashboard (Emergent → Deployment → Custom Domains), add <b>crm.mlily.com.au</b> and click Verify. SSL is provisioned automatically.</li>
              <li>Share <b>https://crm.mlily.com.au</b> with your sales team.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
